// Market system — docs/ARCHITECTURE_M0.md §5.7.
// Continuous-share stand-in for contested design wins (upgrade path: M1).
// Per design-audit findings (node-behind-penalty, release-timing-discontinuity,
// share-model-shape): target share is recomputed FROM SCRATCH every month —
// execution quality and win roll are frozen at release; complexity fit and
// node currency age with the calendar; nodeFactor multiplies OUTSIDE the
// nonlinearity so being a node behind is a documented ×sensitivity, not a cliff.

import type { AttrVector, ChipProject, MarketDef, Product } from "../types.js";
import { ATTR_KEYS } from "../types.js";
import type { TickContext } from "../ctx.js";
import { charge, emit } from "../ctx.js";
import { expectedNodeIndex, getMarket, getNode, nodeIndex } from "../content.js";
import { interpolate } from "../calendar.js";
import { unitCost } from "../formulas/yieldModel.js";
import { clamp } from "../prng.js";
import { rivalPressure } from "./rivals.js";
import { allocationFactor, customerDelta, customerShareFactor, shareBoostFactor, waferCostMult } from "./stakeholders.js";
import { activeTree, frontierAttrs, getTechNode, gradeMult, nodeAttrsAt } from "./tree.js";

function nodeFactor(ctx: TickContext, market: MarketDef, productNodeId: string, m: number): number {
  const expected = expectedNodeIndex(ctx.content, m, market.leadingEdgeLagMonths);
  const behind = Math.max(expected - nodeIndex(ctx.content, productNodeId), -1);
  return Math.pow(market.nodeSensitivity, behind);
}

/** Complexity the market expects at monthIndex m (demand-side Moore's law). */
export function expectedKGates(market: MarketDef, m: number): number {
  return market.expectedKGatesBase * Math.pow(2, m / market.expectedKGatesDoublingMonths);
}

function complexityFit(market: MarketDef, kGates: number, m: number): number {
  return clamp(
    Math.pow(kGates / expectedKGates(market, m), market.complexityFitExp),
    market.complexityFitMin,
    market.complexityFitMax,
  );
}

/** Momentary competitiveness of a product: execution × complexity currency × node currency. */
function competitiveness(ctx: TickContext, market: MarketDef, p: Product, m: number): number {
  return p.qualityExec * complexityFit(market, p.kGates, m) * nodeFactor(ctx, market, p.nodeId, m);
}

/** A product's live attribute vector: its architecture's contributions,
 *  grade-scaled, with obsolescence riders aging shipped silicon (P0, spec §7). */
export function productAttrs(ctx: TickContext, p: { archNodes: Product["archNodes"] }, m: number): AttrVector {
  const out: AttrVector = { perf3d: 0, perf2d: 0, features: 0, compat: 0, efficiency: 0 };
  const tree = activeTree(ctx);
  if (!tree) return out;
  for (const an of p.archNodes) {
    const node = getTechNode(tree, an.id);
    const attrs = nodeAttrsAt(node, m);
    const gm = gradeMult(ctx, an.grade);
    for (const k of ATTR_KEYS) out[k] += (attrs[k] ?? 0) * gm;
  }
  return out;
}

/** Architecture fit vs. the tree frontier, weighted by the segment (P0, spec §7).
 *  = 1 exactly for legacy markets (no attrWeights) or products with no
 *  architecture — the pre-pivot model is the neutral point. */
export function archFitFactor(ctx: TickContext, market: MarketDef, p: { archNodes: Product["archNodes"] }, m: number): number {
  const w = market.attrWeights;
  if (!w || p.archNodes.length === 0) return 1;
  const tree = activeTree(ctx);
  if (!tree) return 1;
  const models = ctx.content.economy.models;
  const frontier = frontierAttrs(ctx, tree, m);
  const attrs = productAttrs(ctx, p, m);
  let num = 0;
  let den = 0;
  for (const k of ATTR_KEYS) {
    // era curves supersede static weights (spec §7): what a segment demands
    // moves with the calendar — D3D compat is a checkbox in '96, the whole
    // purchase decision by '99
    const curve = market.attrWeightCurves?.[k];
    const weight = curve ? interpolate(curve, m) : (w[k] ?? 0);
    if (weight <= 0) continue;
    den += weight;
    const ideal = frontier[k] * models.frontierDiscount;
    // before the frontier has anything on this dimension, any capability is full marks
    const ratio = ideal <= 1e-9 ? (attrs[k] > 0 ? models.archRatioCap : 1) : attrs[k] / ideal;
    num += weight * clamp(ratio, 0, models.archRatioCap);
  }
  if (den <= 0) return 1;
  return clamp(Math.pow(num / den, models.archFitExp), models.archFitMin, models.archFitMax);
}

/** Called from the project pipeline when production qual completes. */
export function releaseProduct(ctx: TickContext, project: ChipProject): void {
  const { s } = ctx;
  const models = ctx.content.economy.models;
  const market = getMarket(ctx.content, project.marketId);

  const haircut = models.errataQualityHaircut * Math.min(project.errataCount, models.errataHaircutCap);
  // garage-era customer discovery (P0, spec §3.2): you understood these buyers
  // before you had a company — a permanent fit bonus on your FIRST part there
  const discovered =
    s.garageLegacy?.discovered.includes(market.id) === true &&
    !s.products.some((p) => p.marketId === market.id && !p.acquired);
  const discoveryBonus = discovered ? 1 + ctx.content.economy.garage.discoveryFitBonus : 1;
  const qualityExec =
    (project.requirementsFit ?? models.feasFitBase) * (project.implQualityRoll ?? 1) * (1 - haircut) * discoveryBonus;

  const incumbent = (s.markets[market.id]?.totalShare ?? 0) >= models.incumbencyShareFloor;
  // per-market lottery width (M3): console-style socket markets are near-binary
  const winSigma = market.winRollSigma ?? models.winRollSigma;
  const winLo = market.winRollSigma !== undefined ? Math.max(0.05, 1 - 2.5 * winSigma) : models.winRollMin;
  const winHi = market.winRollSigma !== undefined ? 1 + 2.5 * winSigma : models.winRollMax;
  const winRoll = clamp(ctx.rng.normal(1, winSigma), winLo, winHi) + (incumbent ? models.incumbencyBonus : 0);

  const product: Product = {
    id: `prd-${project.id}`,
    projectId: project.id,
    marketId: project.marketId,
    nodeId: project.nodeId,
    releasedMonth: s.monthIndex,
    qualityExec,
    designWinRoll: winRoll,
    competitivenessAtRelease: 0, // filled below
    share: 0,
    kGates: project.kGates,
    dieAreaMm2: project.dieAreaMm2 ?? 0,
    active: true,
    channelUnits: 0,
    lastAsp: null,
    royaltyPct: project.royaltyPct,
    acquired: false,
    archNodes: [...project.archNodes],
  };
  product.competitivenessAtRelease = competitiveness(ctx, market, product, s.monthIndex);
  s.products.push(product);
  project.phase = "shipped";
  s.counters.shippedProjects += 1;
  // fresh silicon warms the account; shipping with errata cools it (M3 §11)
  customerDelta(ctx, market.id, "freshSilicon");
  if (project.errataCount > 0) customerDelta(ctx, market.id, "errata");
  emit(
    ctx,
    "productReleased",
    `${product.id} released (C=${product.competitivenessAtRelease.toFixed(2)}, win=${winRoll.toFixed(2)})`,
    {
      productId: product.id,
      projectId: project.id,
      competitiveness: product.competitivenessAtRelease,
      winRoll,
      kGates: project.kGates,
    },
  );
}

export function processMarkets(ctx: TickContext): void {
  const { s, content } = ctx;
  const models = content.economy.models;
  const m = s.monthIndex;

  for (const market of content.markets) {
    const channelPolicy = s.markets[market.id]?.channelMonths ?? 0;
    const products = s.products.filter((p) => p.marketId === market.id && p.active);
    if (products.length === 0) {
      s.markets[market.id] = { totalShare: 0, channelMonths: channelPolicy };
      continue;
    }

    // share dynamics: target recomputed from scratch each month. A stocked channel
    // buys a small availability edge (design §10) — the respectable half of the
    // reason everyone stuffed in 2000. A public listing buys procurement trust:
    // big OEMs prefer suppliers whose balance sheet they can read. M3 adds the
    // ecosystem terms: rival pressure (the survivor's reward when the field
    // thins), the customer relationship, and any negotiated share boosts.
    const channelCfg = content.economy.channel;
    const pressure = rivalPressure(ctx, market);
    const availabilityBonus =
      (1 + channelCfg.shareBonus * Math.min(1, channelPolicy)) *
      (s.stage === "public" ? 1 + models.publicCredibilityShareBonus : 1) *
      pressure *
      customerShareFactor(ctx, market.id) *
      shareBoostFactor(ctx, market.id);
    for (const p of products) {
      const age = m - p.releasedMonth;
      const ageFactor =
        age <= market.productStaleMonths ? 1 : Math.pow(market.staleDecayMonthly, age - market.productStaleMonths);
      const execFit = p.qualityExec * complexityFit(market, p.kGates, m);
      const nf = nodeFactor(ctx, market, p.nodeId, m);
      // architecture fit (P0): recomputed monthly against the MOVING frontier —
      // the world advances, obsolescence riders bite, and shipped bets age
      const af = archFitFactor(ctx, market, p, m);
      const target =
        market.baseCaptureShare *
        Math.pow(clamp(execFit - models.targetShareOffset, 0, models.targetShareSpanCap), models.targetShareExp) *
        nf *
        af *
        p.designWinRoll *
        ageFactor *
        availabilityBonus;
      p.share += (target - p.share) * market.shareRampMonthly * ctx.monthsPerTick;
      if (p.share < 0) p.share = 0;
    }
    const total = products.reduce((a, p) => a + p.share, 0);
    if (total > market.totalShareCap) {
      const scale = market.totalShareCap / total;
      for (const p of products) p.share *= scale;
    }

    // volume, revenue, COGS (one noise draw per product, array order).
    // M3: a capacity crunch with a cold foundry relationship caps what you can
    // actually supply — allocation is politics, and you weren't the priority.
    const tam = interpolate(market.tamUnitsPerMonth, m);
    const refAsp = interpolate(market.refAsp, m);
    const squeeze = allocationFactor(ctx);
    if (squeeze < 1 && products.length > 0) {
      const last = s.askLog["__squeeze"];
      if (last === undefined || m - last >= 12) {
        s.askLog["__squeeze"] = m;
        emit(ctx, "foundrySqueeze", "Capacity crunch: your allocation was cut — priority went to bigger, steadier accounts.", {
          factor: squeeze,
        });
      }
    }
    const waferMult = waferCostMult(ctx);
    for (const p of products) {
      if (p.share <= 1e-6 && p.channelUnits <= 0) continue;
      const noise = ctx.rng.logNormal(0, market.unitsNoiseSigma);
      // end demand: what customers actually consume this month (sell-through)
      const endDemand = tam * p.share * noise * ctx.monthsPerTick * squeeze;

      // price keys on execution quality × complexity currency; node staleness costs
      // SHARE (sockets), not segment price — stale parts sell into value segments.
      // Crowded markets also compress price a little (M3 rival pressure).
      // P0: architecture fit enters price too (dampened) — in a benchmark-driven
      // segment the performance crown IS the premium, and a stale architecture
      // discounts into the value bin. archFit = 1 for legacy markets: no change.
      const priceAf = Math.pow(archFitFactor(ctx, market, p, m), models.aspArchFitExp);
      const cPrice = p.qualityExec * complexityFit(market, p.kGates, m) * priceAf;
      const aspQuality = clamp(
        Math.pow(cPrice / (market.aspQualityRef ?? models.aspQualityRef), models.aspQualityExp),
        models.aspQualityMin,
        models.aspQualityMax,
      );
      const age = m - p.releasedMonth;
      const asp = refAsp * aspQuality * Math.pow(models.aspAgeErosionMonthly, age) * Math.pow(pressure, models.rivalAspExp);

      // price protection (design §10): inventory on distributors' shelves is
      // credited when your ASP falls — HOLDING channel stock in a business with
      // structural price erosion costs real money every month. This is what
      // makes stuffing a loaded gun instead of free revenue timing.
      let protection = 0;
      if (p.channelUnits > 0 && p.lastAsp !== null && asp < p.lastAsp) {
        protection = Math.round(p.channelUnits * (p.lastAsp - asp) * channelCfg.priceProtectRate);
        if (protection > 0) {
          charge(ctx, "priceProtection", -protection, `${p.id} channel price protection`);
          ctx.cogs += protection; // margin-honest: it's a cost of the channel
        }
      }
      p.lastAsp = asp;

      // channel inventory (M2, design §10): revenue books on SELL-IN. The channel
      // stocks toward policy × current demand — pulling revenue forward on the way
      // up, and cancelling your orders while it drains on the way down. The 2001
      // glut wrecks whoever stuffed in 2000.
      const targetChannel = channelPolicy * endDemand;
      const delta = targetChannel - p.channelUnits;
      const shipDelta = Math.min(delta, channelCfg.fillRate * endDemand); // distributors absorb only so fast
      const shipments = Math.max(0, endDemand + shipDelta);
      p.channelUnits = Math.max(0, p.channelUnits + shipments - endDemand);

      const node = getNode(content, p.nodeId);
      const uc = unitCost(node, market, p.dieAreaMm2, m, s.nodes[p.nodeId]!.researchEm, models, waferMult);

      const revenue = Math.round(shipments * asp);
      const cogs = Math.round(shipments * uc.unitCost);
      charge(ctx, "revenue", revenue, `${p.id} ${Math.round(shipments)} units shipped`);
      charge(ctx, "cogs", -cogs, `${p.id} COGS`);
      ctx.revenue += revenue;
      ctx.cogs += cogs;
      // licensed IP takes its cut of every dollar this product ever books (design §6.4)
      if (p.royaltyPct > 0 && revenue > 0) {
        const royalty = Math.round(revenue * p.royaltyPct);
        charge(ctx, "ipRoyalty", -royalty, `${p.id} IP royalties`);
        ctx.cogs += royalty; // margin-honest: it's a per-unit cost of the design
      }
      const sale = {
        productId: p.id,
        marketId: market.id,
        units: shipments,
        endUnits: endDemand,
        channelUnits: p.channelUnits,
        asp,
        unitCost: uc.unitCost,
        revenue,
        cogs,
        recall: 0,
        priceProtection: protection,
      };
      ctx.productSales.push(sale);

      // field-failure exposure (design §6.5): keys on units in CUSTOMERS' hands
      // (sell-through), not what's parked in the channel.
      const reliabilityEm = s.nodes[p.nodeId]!.reliabilityEm;
      const hazard =
        models.fieldFailBase *
        Math.exp(-reliabilityEm / models.reliabilityTauEm) *
        Math.min(2, Math.sqrt(endDemand / models.fieldFailUnitRef)) *
        ctx.monthsPerTick;
      if (hazard > 0 && ctx.rng.bernoulli(hazard)) {
        const recall = Math.round(models.fieldFailCostPerUnit * endDemand * 3); // ~a quarter of volume
        charge(ctx, "recall", -recall, `${p.id} field-failure recall`);
        sale.recall = recall;
        p.qualityExec *= models.fieldFailQualityHit;
        // customers remember who stopped their line (M3 §11 — the meter has memory)
        customerDelta(ctx, market.id, "fieldFailure");
        emit(ctx, "fieldFailure", `${p.id}: field failures in the fleet — recall + reputation damage`, {
          productId: p.id,
          cost: recall,
          reliabilityEm: Math.round(reliabilityEm),
        });
      }
    }

    s.markets[market.id] = {
      totalShare: products.reduce((a, p) => a + p.share, 0),
      channelMonths: channelPolicy,
    };
  }
}
